import { AJUDA_FINANCEIRO } from "@/lib/ajuda/dialogos/financeiro";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { competenciaAtual } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { reais, textoParaCentavos, type RecorrenciaDaLista } from "@/lib/dinheiro";
import { campo, rotulo } from "../estilos";

export function RecorrenciaDialog({ aberto, editando, onFechar }: { aberto: boolean; editando: RecorrenciaDaLista | null; onFechar: () => void }) {
  const utils = trpc.useUtils();
  const categorias = (trpc.financeiro.categorias.listar.useQuery(undefined, { enabled: aberto }).data ?? []).filter((c) => c.tipo === "SAIDA");
  const parceiros = trpc.financeiro.lancamentos.parceiros.useQuery(undefined, { enabled: aberto }).data ?? [];
  const [descricao, setDescricao] = useState("");
  const [valor, setValor] = useState("");
  const [categoriaId, setCategoriaId] = useState("");
  const [dia, setDia] = useState("10");
  const [inicio, setInicio] = useState(competenciaAtual());
  const [fim, setFim] = useState("");
  const [partnerId, setPartnerId] = useState("");

  useEffect(() => {
    if (!aberto) return;
    setDescricao(editando?.descricao ?? "");
    setValor(editando ? reais(editando.valorCents) : "");
    setCategoriaId(editando?.categoria.id ?? "");
    setDia(String(editando?.diaDoVencimento ?? 10));
    setInicio(editando?.inicio ?? competenciaAtual());
    setFim(editando?.fim ?? "");
    setPartnerId(editando?.partner?.id ?? "");
  }, [aberto, editando]);

  const salvar = trpc.financeiro.recorrencias.salvar.useMutation({
    onSuccess: () => {
      toast.success(editando ? "Custo fixo atualizado. Os meses ainda não pagos seguem o novo valor." : "Custo fixo criado.");
      utils.financeiro.invalidate();
      onFechar();
    },
    onError: (e) => toast.error(e.message),
  });

  const valorCents = textoParaCentavos(valor);
  const diaNum = Number(dia);
  const valido = descricao.trim().length >= 2 && valorCents > 0 && !!categoriaId && diaNum >= 1 && diaNum <= 31 && !!inicio && (!fim || fim >= inicio);
  const fixas = categorias.filter((c) => c.natureza === "FIXO" && (c.ativa || c.id === categoriaId));
  const outras = categorias.filter((c) => c.natureza !== "FIXO" && c.linhaDoDre !== "PERDAS_COM_CLIENTES" && (c.ativa || c.id === categoriaId));

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className="max-h-[90dvh] max-w-lg overflow-y-auto border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle ajuda={AJUDA_FINANCEIRO.recorrencia}>{editando ? "Editar custo fixo" : "Novo custo fixo"}</DialogTitle>
          <DialogDescription>Lançado uma vez, aparece como conta a pagar todo mês.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-12">
          <label className="sm:col-span-12">
            <span className={rotulo}>Descrição</span>
            <input value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Ex: Aluguel da sala" className={campo} />
          </label>
          <label className="sm:col-span-5">
            <span className={rotulo}>Valor por mês</span>
            <input value={valor} onChange={(e) => setValor(e.target.value)} inputMode="decimal" placeholder="0,00" className={campo} />
          </label>
          <label className="sm:col-span-7">
            <span className={rotulo}>Categoria</span>
            <select value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)} className={campo}>
              <option value="">Escolha</option>
              <optgroup label="Custos fixos">
                {fixas.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Outras saídas">
                {outras.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </optgroup>
            </select>
          </label>
          <label className="sm:col-span-4">
            <span className={rotulo}>Vence todo dia</span>
            <input type="number" min={1} max={31} value={dia} onChange={(e) => setDia(e.target.value)} className={campo} />
          </label>
          <label className="sm:col-span-4">
            <span className={rotulo}>Primeiro mês</span>
            <input type="month" value={inicio} onChange={(e) => setInicio(e.target.value)} className={campo} />
          </label>
          <label className="sm:col-span-4">
            <span className={rotulo}>Último mês</span>
            <input type="month" value={fim} onChange={(e) => setFim(e.target.value)} className={campo} />
          </label>
          <label className="sm:col-span-12">
            <span className={rotulo}>Fornecedor (opcional)</span>
            <select value={partnerId} onChange={(e) => setPartnerId(e.target.value)} className={campo}>
              <option value="">Nenhum</option>
              {parceiros.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">Sem último mês, segue até você encerrar.</p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onFechar} className="h-9 rounded-md px-4 text-sm text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted">
            Cancelar
          </button>
          <button
            type="button"
            disabled={!valido || salvar.isPending}
            onClick={() =>
              salvar.mutate({ id: editando?.id, descricao, valorCents, categoriaId, diaDoVencimento: diaNum, inicio, fim: fim || null, partnerId: partnerId || null })
            }
            className="inline-flex h-9 items-center gap-2 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
          >
            {salvar.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Salvar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
